import React, { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { motion } from "framer-motion";

interface FormData {
  name: string;
  email: string;
  whatsapp: string;
  city: string;
  objective: string;
  budget: string;
  date: string;
}

const FLOWESTATE_FORWARDER_URL =
  "https://forwarder.flowestate.app/ingest/landing-bavarian-sima";

async function submitToEndpoints(payload: Record<string, unknown>) {
  const flowestateResponse = await fetch(FLOWESTATE_FORWARDER_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!flowestateResponse.ok) {
    throw new Error(
      `Flowestate forwarder failed with ${flowestateResponse.status}`,
    );
  }
}

interface SimaLeadFormProps {
  id: string;
}

const SimaLeadForm: React.FC<SimaLeadFormProps> = ({ id }) => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    whatsapp: "",
    city: "",
    objective: "",
    budget: "",
    date: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        full_name: formData.name,
        email: formData.email,
        phone: formData.whatsapp,
        city: formData.city,
        objective: formData.objective,
        budget: formData.budget,
        preferred_date: formData.date,
        source: "landing-bavarian-sima-2026",
        timestamp: new Date().toISOString(),
        landing_page_url: window.location.href,
      };

      await submitToEndpoints(payload);
      setIsSubmitted(true);
    } catch (error) {
      console.error("Error submitting form:", error);
      alert(
        "No pudimos enviar tu solicitud en este momento. Intenta nuevamente en unos minutos.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <motion.div
        key={`success-${id}`}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="rounded-3xl bg-white/95 backdrop-blur-2xl p-10 text-center shadow-[0_30px_60px_-15px_rgba(0,0,0,0.3)] border border-white/40 ring-1 ring-black/5"
      >
        <div className="mx-auto mb-8 flex h-20 w-20 items-center justify-center rounded-full bg-accent/10 shadow-inner">
          <Check className="h-10 w-10 text-accent" aria-hidden={true} />
        </div>
        <h3 className="mb-4 font-display text-3xl font-semibold tracking-tight text-primary">
          ¡Cita Solicitada!
        </h3>
        <p className="text-text-secondary text-lg font-light leading-relaxed">
          Hemos recibido tus datos correctamente. Nuestro equipo se pondrá en
          contacto pronto para confirmar el horario y los detalles.
        </p>
      </motion.div>
    );
  }

  return (
    <div className="rounded-3xl bg-white/95 backdrop-blur-3xl p-6 md:p-10 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.3)] border border-white/60 relative overflow-hidden ring-1 ring-black/5">
      {/* Soft Top Glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-3 bg-gradient-to-r from-transparent via-accent to-transparent opacity-80 blur-sm"></div>

      <div className="mb-6 text-center md:mb-8">
        <img
          src="/assets/logo-sima-negro.png"
          alt="SIMA Logo"
          className="mx-auto h-16 md:h-24 w-auto object-contain -mt-2 mb-1 drop-shadow-sm"
        />
        <h3 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-primary mb-2 md:mb-3">
          Agenda tu cita privada
        </h3>
        <p className="text-sm md:text-base text-text-secondary font-light leading-relaxed">
          Completa tus datos y te confirmaremos el horario para nuestra reunión
          1:1 en Madrid.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
        <div>
          <label htmlFor={`name-${id}`} className="sr-only">
            Nombre y Apellido
          </label>
          <input
            type="text"
            id={`name-${id}`}
            name="name"
            placeholder="Nombre y Apellido"
            required
            value={formData.name}
            onChange={handleInputChange}
            className="w-full rounded-xl border border-border-light bg-black/[0.02] px-4 py-3.5 md:px-5 md:py-4 text-sm font-medium transition-colors placeholder:text-text-muted hover:bg-black/[0.04] focus:border-accent focus:bg-white focus:ring-2 focus:ring-accent/20"
          />
        </div>
        <div>
          <label htmlFor={`email-${id}`} className="sr-only">
            Email
          </label>
          <input
            type="email"
            id={`email-${id}`}
            name="email"
            placeholder="Correo Electrónico"
            required
            value={formData.email}
            onChange={handleInputChange}
            className="w-full rounded-xl border border-border-light bg-black/[0.02] px-4 py-3.5 md:px-5 md:py-4 text-sm font-medium transition-colors placeholder:text-text-muted hover:bg-black/[0.04] focus:border-accent focus:bg-white focus:ring-2 focus:ring-accent/20"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
          <div>
            <label htmlFor={`whatsapp-${id}`} className="sr-only">
              WhatsApp
            </label>
            <input
              type="tel"
              id={`whatsapp-${id}`}
              name="whatsapp"
              placeholder="WhatsApp"
              required
              value={formData.whatsapp}
              onChange={handleInputChange}
              className="w-full rounded-xl border border-border-light bg-black/[0.02] px-4 py-3.5 md:px-5 md:py-4 text-sm font-medium transition-colors placeholder:text-text-muted hover:bg-black/[0.04] focus:border-accent focus:bg-white focus:ring-2 focus:ring-accent/20"
            />
          </div>
          <div>
            <label htmlFor={`city-${id}`} className="sr-only">
              Ciudad (España)
            </label>
            <input
              type="text"
              id={`city-${id}`}
              name="city"
              placeholder="Ciudad de residencia"
              required
              value={formData.city}
              onChange={handleInputChange}
              className="w-full rounded-xl border border-border-light bg-black/[0.02] px-4 py-3.5 md:px-5 md:py-4 text-sm font-medium transition-colors placeholder:text-text-muted hover:bg-black/[0.04] focus:border-accent focus:bg-white focus:ring-2 focus:ring-accent/20"
            />
          </div>
        </div>

        <div>
          <label htmlFor={`objective-${id}`} className="sr-only">
            ¿Qué buscas?
          </label>
          <select
            id={`objective-${id}`}
            name="objective"
            required
            value={formData.objective}
            onChange={handleInputChange}
            className="w-full rounded-xl border border-border-light bg-black/[0.02] px-4 py-3.5 md:px-5 md:py-4 text-sm font-medium transition-colors focus:border-accent focus:bg-white focus:ring-2 focus:ring-accent/20"
          >
            <option value="" disabled>
              Selecciona tu objetivo principal
            </option>
            <option value="Vivir">Vivir / Residencia</option>
            <option value="Segunda vivienda">
              Segunda vivienda vacacional
            </option>
            <option value="Inversión">Inversión y Rentabilidad</option>
            <option value="Aún evaluando">Aún estoy evaluando opciones</option>
          </select>
        </div>

        <div>
          <label htmlFor={`budget-${id}`} className="sr-only">
            Rango de presupuesto
          </label>
          <select
            id={`budget-${id}`}
            name="budget"
            required
            value={formData.budget}
            onChange={handleInputChange}
            className="w-full rounded-xl border border-border-light bg-black/[0.02] px-4 py-3.5 md:px-5 md:py-4 text-sm font-medium transition-colors focus:border-accent focus:bg-white focus:ring-2 focus:ring-accent/20"
          >
            <option value="" disabled>
              Selecciona rango de presupuesto
            </option>
            <option value="Desde 125k">Desde US$125,000</option>
            <option value="125–160k">US$125k – US$160k</option>
            <option value="160–200k">US$160k – US$200k</option>
            <option value="200k+">Más de US$200k</option>
          </select>
        </div>

        <div>
          <label htmlFor={`date-${id}`} className="sr-only">
            ¿Cuándo te gustaría agendar?
          </label>
          <select
            id={`date-${id}`}
            name="date"
            required
            value={formData.date}
            onChange={handleInputChange}
            className="w-full rounded-xl border border-border-light bg-black/[0.02] px-4 py-3.5 md:px-5 md:py-4 text-sm font-medium transition-colors focus:border-accent focus:bg-white focus:ring-2 focus:ring-accent/20"
          >
            <option value="" disabled>
              ¿Cuándo prefieres agendar?
            </option>
            <option value="20 mayo">Miércoles 20 Mayo (SIMA)</option>
            <option value="21 mayo">Jueves 21 Mayo (SIMA)</option>
            <option value="22 mayo">Viernes 22 Mayo (SIMA)</option>
            <option value="23 mayo">Sábado 23 Mayo (SIMA)</option>
            <option value="Virtual">Prefiero reunión Virtual</option>
          </select>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full mt-5 md:mt-6 flex items-center justify-center rounded-xl bg-accent px-6 py-4.5 md:py-5 text-sm font-bold uppercase tracking-wider text-white shadow-[0_10px_20px_-10px_rgba(2,132,199,0.5)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-accent-dark hover:shadow-[0_20px_25px_-10px_rgba(2,132,199,0.6)] disabled:pointer-events-none disabled:opacity-70"
        >
          {isSubmitting ? (
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          ) : (
            "Confirmar Cita Privada"
          )}
        </button>
      </form>
    </div>
  );
};

export default SimaLeadForm;
